/**
 * GDPR-only helpers for questions linked to backend automation via `system_data`.
 *
 * Kept outside the shared Questionnaire package — other consumers do not use this field.
 */

/**
 * Impact copy for known `system_data` keys (backend handoff — exact wording).
 * Alias keys share the same message as their canonical key.
 */
export const SYSTEM_DATA_IMPACT_BY_KEY = Object.freeze({
    description:
        'Case description is no longer filled from this questionnaire',
    first_name:
        'Reporter first name no longer syncs from answers (third-party)',
    last_name:
        'Reporter last name no longer syncs from answers (third-party)',
    special_categories:
        'DPIA high-risk detection for special categories stops',
    data_processed_no_reason:
        'DPIA high-risk detection for missing legal reason stops',
    data_processed_vulnerable_persons:
        'DPIA high-risk detection for vulnerable persons stops',
    new_technologies:
        'Auto AI Risk Assessment creation from this answer stops',
    data_transfer_outside_eu:
        'DPIA high-risk detection for transfers outside EU stops',
    actor_role:
        'EU AI Act risk classification incomplete (actor role)',
    eu_market:
        'EU AI Act risk classification incomplete (EU market)',
    unacceptable_risk:
        'EU AI Act prohibited-risk check unused',
    annex_iii:
        'EU AI Act Annex III high-risk check unused',
    narrow_procedural_exemption:
        'EU AI Act procedural exemption unused',
    gpai:
        'EU AI Act GPAI classification unused',
    systemic_risk:
        'EU AI Act systemic-risk classification unused',
    biometric_informed:
        'EU AI Act biometric-informed check unused',
    applicationArea:
        'EU AI Act application-area classification unused',
    primary_application_area:
        'EU AI Act application-area classification unused',
    interactionType:
        'EU AI Act interaction classification unused',
    system_interaction:
        'EU AI Act interaction classification unused',
    biometricSurveillance:
        'EU AI Act biometric surveillance check unused',
    public_biometric_surveillance:
        'EU AI Act biometric surveillance check unused',
    humanOversight:
        'EU AI Act human-oversight check unused',
    human_oversight:
        'EU AI Act human-oversight check unused',
    userInformed:
        'EU AI Act user-information check unused',
    user_information:
        'EU AI Act user-information check unused',
    loggingMechanisms:
        'EU AI Act logging obligation check unused',
    logging_audit:
        'EU AI Act logging obligation check unused',
    testingPerformed:
        'EU AI Act testing obligation check unused',
    system_testing:
        'EU AI Act testing obligation check unused',
    dataCollectionLawful:
        'EU AI Act lawful collection check unused',
    data_collection_lawfulness:
        'EU AI Act lawful collection check unused',
    datasetValidation:
        'EU AI Act training-data validation check unused',
    training_datasets_validation:
        'EU AI Act training-data validation check unused'
});

export const SYSTEM_DATA_DELETE_CANCELLED = 'SYSTEM_DATA_DELETE_CANCELLED';

/**
 * Read a meaningful system_data / systemData string from a Designer or API question.
 *
 * @param {Object} [item]
 * @returns {string|null}
 */
export const getQuestionSystemData = (item) => {
    if (!item || typeof item !== 'object') {
        return null;
    }

    const raw = item.systemData ?? item.system_data;

    if (typeof raw !== 'string') {
        return null;
    }

    const trimmed = raw.trim();

    return trimmed.length > 0 ? trimmed : null;
};

/**
 * @param {Object} [item]
 * @returns {boolean}
 */
export const hasQuestionSystemData = (item) => {
    return getQuestionSystemData(item) !== null;
};

/**
 * Feature-impact sentence for a system_data key (exact backend wording).
 * Unknown keys use the documented generic fallback.
 *
 * @param {string} systemDataKey
 * @returns {string}
 */
export const getSystemDataImpactMessage = (systemDataKey) => {
    if (
        systemDataKey &&
        Object.prototype.hasOwnProperty.call(
            SYSTEM_DATA_IMPACT_BY_KEY,
            systemDataKey
        )
    ) {
        return SYSTEM_DATA_IMPACT_BY_KEY[systemDataKey];
    }

    return `Automated processing linked to system key "{{system_data}}" will stop working.`;
};

/**
 * User-facing copy for the GDPR system_data delete warning (exact backend wording).
 *
 * @param {string} systemDataKey
 * @returns {{
 *   lead: string,
 *   impact: string,
 *   consequence: string,
 *   confirm: string,
 *   messageValues?: { system_data: string }
 * }}
 */
export const getSystemDataDeletionCopy = (systemDataKey) => {
    const knownImpact = Object.prototype.hasOwnProperty.call(
        SYSTEM_DATA_IMPACT_BY_KEY,
        systemDataKey
    )
        ? SYSTEM_DATA_IMPACT_BY_KEY[systemDataKey]
        : null;

    const copy = {
        lead: 'This question is linked to a system feature.',
        impact: knownImpact || getSystemDataImpactMessage(systemDataKey),
        consequence:
            'If you remove it, that feature may stop working until another question uses the same system key.',
        confirm: 'Do you still want to delete this question?'
    };

    if (!knownImpact) {
        copy.messageValues = { system_data: systemDataKey };
    }

    return copy;
};

/**
 * Build shared Designer `getDeleteConfirmation` config for a GDPR question.
 * Returns `null` when no special confirmation is needed (default Designer copy).
 *
 * @param {{ type?: string, item?: Object, context?: Object }} payload
 * @returns {{
 *   required: true,
 *   title: string,
 *   message: string[],
 *   confirmText: string,
 *   cancelText: string,
 *   messageValues?: { system_data: string }
 * }|null}
 */
export const getGdprSystemDataDeleteConfirmation = ({ type, item, context } = {}) => {
    const isOptionChild = type === 'question' && context?.childIndex != null;

    if (type !== 'question' || isOptionChild) {
        return null;
    }

    const systemDataKey = getQuestionSystemData(item);

    if (!systemDataKey) {
        return null;
    }

    const copy = getSystemDataDeletionCopy(systemDataKey);

    const config = {
        required: true,
        title: copy.lead,
        message: [copy.impact, copy.consequence, copy.confirm],
        confirmText: 'Delete',
        cancelText: 'Cancel'
    };

    if (copy.messageValues) {
        config.messageValues = copy.messageValues;
    }

    return config;
};

/**
 * @param {Error|*} error
 * @returns {boolean}
 */
export const isSystemDataDeletionCancelled = (error) => {
    return (
        error instanceof Error &&
        error.message === SYSTEM_DATA_DELETE_CANCELLED
    );
};
